import React, { useEffect, useState } from 'react';
import { api, fileUrl } from '../lib/api';
import { toast } from 'sonner';
import { BookOpen, Upload, Trash2, Download } from 'lucide-react';

const CLASSES = ['Preparatory', 'Lower Nursery', 'Upper Nursery', 'Transition', 'Class I', 'Class II',
  'Class III', 'Class IV', 'Class V', 'Class VI', 'Class VII', 'Class VIII', 'Class IX'];

export default function SyllabusLibrary() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', class_name: '', subject: '', session: '2026-27', version: '1.0', file: null });
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/syllabus').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!form.file || !form.title || !form.class_name) { toast.error('Title, class and file are required'); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries({ title: form.title, class_name: form.class_name, subject: form.subject, session: form.session, version: form.version }).forEach(([k, v]) => fd.append(k, v));
    fd.append('file', form.file);
    try {
      await api.post('/syllabus', fd);
      toast.success('Syllabus uploaded');
      setForm({ title: '', class_name: '', subject: '', session: '2026-27', version: '1.0', file: null });
      load();
    } catch (e) { toast.error('Upload failed'); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/syllabus/${id}`); load(); };
  const inputCls = 'w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-action';

  return (
    <div className="space-y-5" data-testid="syllabus-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#172033]">Syllabus Library</h1>
        <p className="text-sm text-[#64748B]">Manage admission test syllabi by session, class, subject and version.</p>
      </div>
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
        <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-navy">Upload Syllabus</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <input data-testid="syllabus-title" className={inputCls} placeholder="Title e.g. Class IV Admission Test Syllabus" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <select data-testid="syllabus-class" className={inputCls} value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })}>
            <option value="">Select class</option>{CLASSES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className={inputCls} placeholder="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <input className={inputCls} placeholder="Session" value={form.session} onChange={(e) => setForm({ ...form, session: e.target.value })} />
          <input className={inputCls} placeholder="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
          <input data-testid="syllabus-file" type="file" className={inputCls} onChange={(e) => setForm({ ...form, file: e.target.files[0] })} />
        </div>
        <button data-testid="syllabus-upload-btn" disabled={busy} onClick={upload}
          className="mt-3 flex items-center gap-2 rounded-lg bg-action px-4 py-2 text-sm font-semibold text-white hover:bg-action-hover disabled:opacity-60">
          <Upload size={15} /> {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((s) => (
          <div key={s.syllabus_id} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
            <div className="flex items-start justify-between">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><BookOpen size={17} /></span>
              <button onClick={() => del(s.syllabus_id)} className="text-[#94A3B8] hover:text-[#DC2626]"><Trash2 size={15} /></button>
            </div>
            <p className="mt-2 font-semibold text-[#172033]">{s.title}</p>
            <p className="text-xs text-[#64748B]">{s.class_name} · {s.session} · v{s.version}</p>
            {s.file_id && <a href={fileUrl(s.file_id)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-action hover:underline"><Download size={13} /> Download</a>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#94A3B8]">No syllabi uploaded yet.</p>}
      </div>
    </div>
  );
}
